// src/prototype/pages/EAUseCaseBriefPage.tsx
// ============================================================
// EA USE CASE DECISION BRIEF  (/ea/use-case/:id/brief)
//
// Verdict-led single page: executive decision and justifying numbers lead;
// analytical depth sits in on-demand accordions. Replaces the former 7-tab
// profile. API mapping is unchanged (evaluation + opportunity list).
// ============================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  TrendingUp, BarChart3, Zap, Target, Shield, Calendar,
  FileText, BookOpen, ArrowLeft, ChevronRight, AlertTriangle, ExternalLink,
} from 'lucide-react';
import { EAJourneyBar } from '../components/EAJourneyBar';
import { EAApiErrorPanel } from '../components/EAApiErrorPanel';
import { ScoreBar } from '../components/ScoreBar';
import { SectionCard } from '../components/SectionCard';
import { RiskCard } from '../components/RiskCard';
import { type EffortTag } from '../components/EffortTagPill';
import { Collapse } from '../components/Collapse';
import { StepList } from '../components/StepList';
import { RoadmapRow, PathToValueList, roadmapProgressSummary, type RoadmapPhaseStatus, toApiMilestoneStatus } from '../components/RoadmapRow';
import { BriefPrintPreview, type BriefPrintSnapshot } from '../components/BriefPrintPreview';
import { EAUseCaseHubShell, type HubView } from '../components/hub/EAUseCaseHubShell';
import { useHubChrome } from '../contexts/HubChromeContext';
import { EA_CLIENT_ID } from '../constants/eaClient';
import {
  fetchEvaluation,
  fetchOpportunities,
  fetchOpportunity,
  fetchRoiModel,
  invalidateEvaluation,
  coerceImmediateNextSteps,
  ensureCatalogBriefSnapshot,
  isPlaceholderUseCaseTitle,
  normalizeAlignmentEvidence,
  parseMonthsFromStart,
  peekCatalogBriefSnapshot,
  peekEvaluation,
  peekOpportunities,
  selectUseCase,
  sortMilestonesByTimeline,
  UCE_CATALOG_FILTERS,
  type AlignmentEvidencePoint,
  type ImmediateNextStep,
} from '../services/useCaseExplorerService';
import { updateMilestone, CommandCenterError } from '../services/commandCenterService';
import { fetchScoringRationale } from '../services/scoringRationaleService';
import { useEA } from '../contexts/EAContext';
import { useDemoPlan } from '../../hooks/useDemoPlan';
import {
  SEVERITY_DOT,
  verdictFromScore,
  recommendationFromVerdict,
  parseRecommendation,
  type VerdictKey,
  type RecommendationKey,
} from '../theme/decisionTokens';
import { buildDrilldownNav, readDrilldownNav, resolveDrilldownExit, type EaDrilldownNavState } from '../utils/eaDrilldownNav';
import { resolveScoreFreshness } from '../utils/formatLastComputed';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

/** Global use case catalog size — always 175, never derived from filtered set */
const TOTAL_PORTFOLIO_SIZE = 175;

/** $ amounts in millions — always 2 decimal places. */
const fmtM2 = (n: number) => (Number.isFinite(n) ? n : 0).toFixed(2);
/** Scores and other 0–100 style numbers — 2 decimal places. */
const fmtNum2 = (n: number) => (Number.isFinite(n) ? n : 0).toFixed(2);

/** Markdown blocks from evaluation API — typography aligned to brief sections */
const PROFILE_MD_COMPONENTS: Components = {
  h1: ({ children, ...props }) => <h1 className="text-lg font-bold text-[#18365E] mt-4 mb-2 first:mt-0" {...props}>{children}</h1>,
  h2: ({ children, ...props }) => <h2 className="text-base font-bold text-[#18365E] mt-4 mb-2 first:mt-0" {...props}>{children}</h2>,
  h3: ({ children, ...props }) => <h3 className="text-sm font-bold text-[#18365E] mt-3 mb-1.5 first:mt-0" {...props}>{children}</h3>,
  h4: ({ children, ...props }) => <h4 className="text-xs font-bold uppercase tracking-wide text-[#18365E] mt-3 mb-1 first:mt-0" {...props}>{children}</h4>,
  p: ({ children, ...props }) => <p className="text-sm text-[#717182] leading-relaxed mb-2 last:mb-0" {...props}>{children}</p>,
  ul: ({ children, ...props }) => <ul className="mb-3 ml-1 list-disc space-y-1 pl-5 text-sm text-[#717182] marker:text-[#18365E]/50" {...props}>{children}</ul>,
  ol: ({ children, ...props }) => <ol className="mb-3 ml-1 list-decimal space-y-1 pl-5 text-sm text-[#717182] marker:font-semibold marker:text-[#18365E]" {...props}>{children}</ol>,
  li: ({ children, ...props }) => <li className="leading-relaxed" {...props}>{children}</li>,
  strong: ({ children, ...props }) => <strong className="font-semibold text-[#18365E]" {...props}>{children}</strong>,
  em: ({ children, ...props }) => <em className="italic text-[#717182]" {...props}>{children}</em>,
  a: ({ children, ...props }) => (
    <a className="font-medium text-[#18365E] underline decoration-[#18365E]/30 underline-offset-2 hover:decoration-[#18365E]" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  ),
  hr: (props) => <hr className="my-4 border-[#e5e7eb]" {...props} />,
  blockquote: ({ children, ...props }) => (
    <blockquote className="my-2 border-l-2 border-[#18365E]/25 pl-3 text-sm italic text-[#717182]" {...props}>{children}</blockquote>
  ),
  pre: ({ children, ...props }) => (
    <pre className="my-3 overflow-x-auto rounded-lg border border-[#e5e7eb] bg-[#F5F7FA] p-3 text-xs text-[#18365E]" {...props}>{children}</pre>
  ),
  code: ({ className, children, ...props }) => {
    const inline = !className;
    if (inline) {
      return <code className="rounded bg-[#F5F7FA] px-1 py-0.5 text-[13px] font-medium text-[#18365E]" {...props}>{children}</code>;
    }
    return <code className={className} {...props}>{children}</code>;
  },
};

const NO_PROFILE_VALUE = 'No value';

function isEmptyProfileContent(value: string | undefined | null): boolean {
  const s = String(value ?? '').trim();
  return !s || s === NO_PROFILE_VALUE;
}

/** KPI tile display — `—` only when the API reports no value; a reported 0 is shown as 0 (DMD-1159). */
const KPI_UNAVAILABLE = '—';
function fmtKpiMoneyM(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return KPI_UNAVAILABLE;
  return `$${fmtM2(n)}M`;
}
function fmtKpiAnnualBenefit(roiRange: string, annualBenefitM: number | null | undefined): string {
  if (!isEmptyProfileContent(roiRange)) return roiRange;
  return fmtKpiMoneyM(annualBenefitM);
}
function fmtKpiScore(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return KPI_UNAVAILABLE;
  return `${fmtNum2(n)} / 100`;
}
function fmtKpiTimeToValue(value: string | undefined | null): string {
  const s = String(value ?? '').trim();
  if (!s || isEmptyProfileContent(s) || /^0\s*months?$/i.test(s)) return KPI_UNAVAILABLE;
  return s;
}
function fmtKpiRank(rank: number, portfolioSize: number): string {
  if (!Number.isFinite(rank) || rank <= 0) return KPI_UNAVAILABLE;
  return `#${rank} of ${portfolioSize}`;
}

function normalizePriorityLabel(value: string | undefined | null): string {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const normalized = s.toLowerCase();
  if (normalized === 'no value' || normalized === 'n/a' || normalized === 'na' || normalized === 'none') {
    return '';
  }
  return s;
}

function pickProfileContent(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const s = String(candidate ?? '').trim();
    if (s && s !== NO_PROFILE_VALUE) return s;
  }
  return '';
}

function normalizeRequirementType(raw: unknown): RequirementItem['type'] {
  const value = String(raw ?? '').toLowerCase();
  if (value.includes('data')) return 'data';
  if (value.includes('govern') || value.includes('legal') || value.includes('compliance')) return 'governance';
  if (value.includes('org') || value.includes('change') || value.includes('staff') || value.includes('human')) {
    return 'organizational';
  }
  return 'technical';
}

function mapRequirementRow(raw: unknown): RequirementItem | null {
  if (typeof raw === 'string') {
    const details = raw.trim();
    if (!details || isEmptyProfileContent(details)) return null;
    return {
      type: 'technical',
      name: 'Implementation Requirement',
      details,
      critical: false,
    };
  }

  if (!raw || typeof raw !== 'object') return null;

  const row = raw as Record<string, unknown>;
  const name = String(row.name ?? row.title ?? row.label ?? '').trim();
  const details = String(row.details ?? row.description ?? row.summary ?? row.value ?? '').trim();
  if (!name && !details) return null;

  const reqType = row.type ?? row.req_type ?? row.category;
  // Criticality comes only from an explicit API flag (DMD-1157) — never inferred from type.
  const criticalRaw = row.critical ?? row.is_critical ?? row.criticality;
  const criticalText = String(criticalRaw ?? '').toLowerCase();
  const critical = criticalRaw === true || criticalText === 'true' || criticalText === 'critical';

  return {
    type: normalizeRequirementType(reqType ?? name),
    name: name || 'Implementation Requirement',
    details: details || name,
    critical,
  };
}

function mapRoadmapRequirements(
  roadmapTab: Record<string, unknown>,
  feasibilityTab?: Record<string, unknown>,
  payloadRoot?: Record<string, unknown>,
): RequirementItem[] {
  const explicitLists = [
    // Canonical structured list on the evaluation payload (DMD-1156).
    payloadRoot?.implementation_requirements,
    payloadRoot?.implementationRequirements,
    roadmapTab.requirements,
    roadmapTab.implementation_requirements,
    roadmapTab.implementationRequirements,
    roadmapTab.dependencies,
    feasibilityTab?.requirements,
    feasibilityTab?.implementation_requirements,
    feasibilityTab?.implementationRequirements,
  ];

  for (const list of explicitLists) {
    if (!Array.isArray(list) || list.length === 0) continue;
    const mapped = list.map(mapRequirementRow).filter((item): item is RequirementItem => item != null);
    if (mapped.length > 0) return mapped;
  }

  return [];
}

const ProfileMarkdown: React.FC<{
  source: string;
  className?: string;
  emptyMessage?: string;
}> = ({ source, className, emptyMessage }) => {
  const s = String(source ?? '').trim();
  if (isEmptyProfileContent(s)) {
    return (
      <p className="text-sm text-[#717182] leading-relaxed italic">
        {emptyMessage ?? 'Information for this section is not yet available for this use case.'}
      </p>
    );
  }
  return (
    <div className={className ?? 'max-w-none'}>
      <ReactMarkdown components={PROFILE_MD_COMPONENTS}>{s}</ReactMarkdown>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface RiskItem {
  name: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  likelihood: 'Very High' | 'High' | 'Medium' | 'Low' | 'Very Low';
  description: string;
  mitigation: string;
  owner: string;
  /** When present (API), used for stable list keys */
  risk_id?: string;
}

function normalizeRiskSeverity(raw: unknown): RiskItem['severity'] {
  const s = String(raw ?? 'Medium').trim().toLowerCase();
  if (s === 'critical' || s === 'crit' || s === 'catastrophic') return 'Critical';
  if (s === 'high' || s === 'severe') return 'High';
  if (s === 'low' || s === 'minimal') return 'Low';
  return 'Medium';
}

const RISK_SEVERITY_RANK: Record<RiskItem['severity'], number> = {
  Low: 1,
  Medium: 2,
  High: 3,
  Critical: 4,
};

/** Highest severity across the register — not the first row (DMD-1175). */
function topRiskSeverity(risks: Pick<RiskItem, 'severity'>[]): RiskItem['severity'] | null {
  if (!risks.length) return null;
  return risks.reduce<RiskItem['severity']>((top, risk) => {
    return RISK_SEVERITY_RANK[risk.severity] > RISK_SEVERITY_RANK[top] ? risk.severity : top;
  }, risks[0].severity);
}

function normalizeRiskLikelihood(raw: unknown): RiskItem['likelihood'] {
  const s = String(raw ?? 'Medium').trim().toLowerCase();
  if (s.includes('very high') || s === 'veryhigh' || s === 'almost certain') return 'Very High';
  if (s.includes('very low') || s === 'rare') return 'Very Low';
  if (s === 'high' || s === 'likely') return 'High';
  if (s === 'low' || s === 'unlikely') return 'Low';
  return 'Medium';
}

interface MilestoneItem {
  milestone_id?: string | null;
  label: string;
  months_from_start: number;
  value_note: string;
  expected_outcome: string;
  /** Null when no tracking row yet — UI defaults to Todo (DMD-1170). */
  status: string | null;
  /** 0–100 from the API, null when not reported (DMD-1155). */
  percent_complete: number | null;
  owner: string;
}

interface RequirementItem {
  type: 'data' | 'technical' | 'organizational' | 'governance';
  name: string;
  details: string;
  critical: boolean;
}

/** Financial figures are null when the API does not report them (DMD-1159). */
interface ProfileROI {
  annual_benefit_m: number | null;
  implementation_cost_m: number | null;
  payback_months: number | null;
  npv_3yr_m: number | null;
  confidence: number;
}

interface ActionItem { title: string; description: string; owner: string; }

interface ProfileData {
  use_case_id: string;
  title: string;
  category: string;
  priority_label: string;
  recommendation: RecommendationKey;
  /** False when the verdict came from client-side score thresholds, not the API (DMD-1160). */
  recommendation_from_api: boolean;
  executive_summary: string;
  rationale: string;
  use_case_description: string;
  strategic_narrative: string;
  strategic_context: string;
  organizational_impact: string;
  operational_impact: string;
  clinical_impact: string;
  stakeholder_alignment: string;
  alignment_evidence: AlignmentEvidencePoint[];
  roi_range: string;
  time_to_roi: string;
  implementation_effort: 'Low' | 'Medium' | 'High';
  value_score: number;
  implementation_score: number;
  total_score: number;
  rank: number;
  /** Authoritative percentile from the API; null hides the percentile label (DMD-1161). */
  percentile: number | null;
  /** Per-use-case score freshness from the evaluation payload (DMD-1158). */
  last_computed_at: string | null;
  last_computed_source: string | null;
  roi: ProfileROI;
  roi_narrative: string;
  roi_assumptions: { label: string; value: string }[];
  feasibility: {
    complexity_summary: string;
    technology_readiness: string;
    data_availability: string;
    organizational_readiness: string;
    integration_complexity: string;
    operational_dependencies: string;
    change_management: string;
  };
  risks: RiskItem[];
  milestones: MilestoneItem[];
  requirements: RequirementItem[];
  next_steps: ImmediateNextStep[];
  actions: { immediate: ActionItem[]; near_term: ActionItem[]; strategic: ActionItem[] };
}

const EFFORT_TO_TAG: Record<ProfileData['implementation_effort'], EffortTag> = {
  Low: 'Quick Win',
  Medium: 'Moderate',
  High: 'High',
};

function verdictFromRecommendation(rec: ProfileData['recommendation']): VerdictKey {
  if (rec === 'PROCEED') return 'green';
  if (rec === 'EVALUATE') return 'cond';
  return 'hold';
}

export default function EAUseCaseBriefPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = readDrilldownNav(location.state);
  const { isFreemium } = useDemoPlan();
  const { finalistIds, addFinalist } = useEA();
  const { setChrome, setExportHandler } = useHubChrome();

  const clientId = EA_CLIENT_ID;
  const navSnapshot =
    navState.briefSnapshot && String(navState.briefSnapshot.id) === String(id)
      ? navState.briefSnapshot
      : null;

  const cachedEval = id && !Number.isNaN(parseInt(id, 10)) ? peekEvaluation(clientId, id) : null;
  const [evalData, setEvalData] = useState<any>(() => cachedEval?.data ?? null);
  const [evalLoading, setEvalLoading] = useState(() => !cachedEval?.data);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [resolvedUseCaseName, setResolvedUseCaseName] = useState<string | null>(
    () => navSnapshot?.title ?? null,
  );
  const [resolvedCategory, setResolvedCategory] = useState<string | null>(
    () => navSnapshot?.category ?? null,
  );
  const [resolvedTotalScore, setResolvedTotalScore] = useState<number | null>(
    () => (typeof navSnapshot?.totalScore === 'number' ? navSnapshot.totalScore : null),
  );
  const [resolvedValueBand, setResolvedValueBand] = useState<string | null>(
    () => (navSnapshot?.valueBand ? String(navSnapshot.valueBand) : null),
  );
  const [rankedIds, setRankedIds] = useState<string[]>(() =>
    Array.isArray(navState.rankedIds) ? navState.rankedIds.map(String) : [],
  );
  const [catalogTotal, setCatalogTotal] = useState(() =>
    typeof navState.catalogTotal === 'number' ? navState.catalogTotal : TOTAL_PORTFOLIO_SIZE,
  );
  const [selectionSyncError, setSelectionSyncError] = useState<string | null>(null);
  const [shareLabel, setShareLabel] = useState('Share');
  const [metricsLastComputedAt, setMetricsLastComputedAt] = useState<string | null>(null);
  const [metricsLastComputedSource, setMetricsLastComputedSource] = useState<string | null>(null);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  /** Frozen at Export click so PDF matches on-screen data at that moment (DMD-1100). */
  const [printSnapshot, setPrintSnapshot] = useState<BriefPrintSnapshot | null>(null);
  const [printGeneratedAt, setPrintGeneratedAt] = useState<Date | null>(null);
  /** Optimistic Path to Value status overrides keyed by milestone_id (DMD-1170). */
  const [milestoneStatusOverrides, setMilestoneStatusOverrides] = useState<Record<string, string>>({});
  const [savingMilestoneIds, setSavingMilestoneIds] = useState<Set<string>>(() => new Set());
  const [milestoneStatusError, setMilestoneStatusError] = useState<string | null>(null);
  /** ROI Model base net 3-year value (M$) — same source as ROI KPI card (DMD-1093). */
  const [roiModelNet3YearM, setRoiModelNet3YearM] = useState<number | null>(null);

  const isNumericUseCaseId = Boolean(id) && !isNaN(parseInt(id || '', 10));

  useEffect(() => {
    // Hub owns scroll when embedded — avoid jumping on tab remount.
    if (embedded) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id, embedded]);

  useEffect(() => {
    setMilestoneStatusOverrides({});
    setSavingMilestoneIds(new Set());
    setMilestoneStatusError(null);
    setRoiModelNet3YearM(null);
    setPrintPreviewOpen(false);
    setPrintSnapshot(null);
    setPrintGeneratedAt(null);
  }, [id]);

  // Seed header fields from Evaluate navigation state when present.
  // DMD-1178: do NOT clear the title when briefSnapshot is missing — that wiped
  // names after returning from Scoring Rationale / ROI and fell back to "Use Case N".
  useEffect(() => {
    const snap =
      navState.briefSnapshot && String(navState.briefSnapshot.id) === String(id)
        ? navState.briefSnapshot
        : null;
    if (!snap) return;
    if (snap.title) setResolvedUseCaseName(snap.title);
    if (snap.category) setResolvedCategory(snap.category);
    if (typeof snap.totalScore === 'number') setResolvedTotalScore(snap.totalScore);
    if (snap.valueBand) setResolvedValueBand(String(snap.valueBand));
  }, [id, navState.briefSnapshot]);

  // Reset header fields when the use-case id changes (prev/next).
  // Prefer nav snapshot, else durable/ranked catalog. If the 60s TTL is cold, resolve async
  // so we never stick on "Use Case N" (DMD-1180).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const snap =
      navState.briefSnapshot && String(navState.briefSnapshot.id) === String(id)
        ? navState.briefSnapshot
        : null;
    const fromCatalog = peekCatalogBriefSnapshot(clientId, id);
    const seededTitle = [snap?.title, fromCatalog?.title].find(
      (t) => t && !isPlaceholderUseCaseTitle(t),
    ) as string | undefined;

    setResolvedUseCaseName(seededTitle ?? null);
    setResolvedCategory(snap?.category || fromCatalog?.category || null);
    setResolvedTotalScore(
      typeof snap?.totalScore === 'number'
        ? snap.totalScore
        : typeof fromCatalog?.totalScore === 'number'
          ? fromCatalog.totalScore
          : null,
    );
    setResolvedValueBand(
      snap?.valueBand
        ? String(snap.valueBand)
        : fromCatalog?.valueBand
          ? String(fromCatalog.valueBand)
          : null,
    );

    if (seededTitle || !clientId) return;

    void ensureCatalogBriefSnapshot(clientId, id).then((resolved) => {
      if (cancelled || !resolved?.title) return;
      setResolvedUseCaseName((prev) =>
        !prev || isPlaceholderUseCaseTitle(prev) ? resolved.title : prev,
      );
      if (resolved.category) setResolvedCategory((prev) => prev || resolved.category);
      if (typeof resolved.totalScore === 'number') {
        setResolvedTotalScore((prev) => (prev == null ? resolved.totalScore! : prev));
      }
      if (resolved.valueBand) setResolvedValueBand((prev) => prev || resolved.valueBand!);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only on id change
  }, [id]);

  // Critical path: evaluation only. Title/score come from nav snapshot or evaluation payload —
  // no separate fetchOpportunity (that was a duplicate of Evaluate + evaluation.use_case).
  useEffect(() => {
    if (!id || !clientId || !isNumericUseCaseId) return;
    const ucId = parseInt(id, 10);
    let cancelled = false;

    const applyEval = (data: Record<string, unknown>) => {
      // DMD-1169: the /evaluation header does not always carry valueScore /
      // implementationScore, so the normalized payload arrives with 0/100.
      // Backfill from the catalog cache (single-opportunity endpoint) when the
      // evaluation payload leaves those fields empty.
      const catalogList = peekOpportunities(String(clientId), UCE_CATALOG_FILTERS);
      const catalogRow = catalogList?.data?.find(
        (o) => String(o.use_case_id) === String(ucId),
      );

      const dataAny = data as {
        use_case?: Record<string, unknown>;
        tabs?: { overview?: { key_metrics?: Record<string, unknown> } };
      };
      const ucRaw = dataAny.use_case || {};
      const keyMetrics = dataAny.tabs?.overview?.key_metrics || {};
      const hasUsableScore = (v: unknown) =>
        typeof v === 'number' && Number.isFinite(v) && v > 0;

      const backfillFromCatalog = () => {
        if (!catalogRow) return;
        if (!hasUsableScore(ucRaw.value_score)) ucRaw.value_score = catalogRow.value_score;
        if (!hasUsableScore(ucRaw.implementation_score)) {
          ucRaw.implementation_score = catalogRow.implementation_score;
        }
        if (!hasUsableScore(ucRaw.total_score)) ucRaw.total_score = catalogRow.total_score;
        if (!hasUsableScore(keyMetrics.value_score)) keyMetrics.value_score = catalogRow.value_score;
        if (!hasUsableScore(keyMetrics.implementation_score)) {
          keyMetrics.implementation_score = catalogRow.implementation_score;
        }
        if (!hasUsableScore(keyMetrics.total_score)) keyMetrics.total_score = catalogRow.total_score;
      };

      backfillFromCatalog();

      // Fallback: when the catalog cache is cold (e.g. direct deep-link), pull
      // the single opportunity and re-backfill so the Scoring Profile shows real
      // numbers instead of 0/100.
      const needsOppBackfill =
        !hasUsableScore(ucRaw.value_score) || !hasUsableScore(ucRaw.implementation_score);
      if (needsOppBackfill) {
        fetchOpportunity(String(clientId), ucId)
          .then((opportunity) => {
            if (cancelled) return;
            if (!hasUsableScore(opportunity.value_score)) return;
            setEvalData((prev) => {
              if (!prev) return prev;
              const next = { ...prev } as typeof dataAny;
              const useCase = { ...(next.use_case || {}) };
              const tabsCopy = next.tabs ? { ...next.tabs } : {};
              const overviewCopy = tabsCopy.overview ? { ...tabsCopy.overview } : {};
              const km = (overviewCopy.key_metrics
                ? { ...overviewCopy.key_metrics }
                : {}) as Record<string, unknown>;
              if (!hasUsableScore(useCase.value_score)) useCase.value_score = opportunity.value_score;
              if (!hasUsableScore(useCase.implementation_score)) {
                useCase.implementation_score = opportunity.implementation_score;
              }
              if (!hasUsableScore(useCase.total_score)) useCase.total_score = opportunity.total_score;
              if (!hasUsableScore(km.value_score)) km.value_score = opportunity.value_score;
              if (!hasUsableScore(km.implementation_score)) {
                km.implementation_score = opportunity.implementation_score;
              }
              if (!hasUsableScore(km.total_score)) km.total_score = opportunity.total_score;
              overviewCopy.key_metrics = km;
              tabsCopy.overview = overviewCopy;
              next.tabs = tabsCopy as typeof next.tabs;
              next.use_case = useCase;
              return next as unknown as Record<string, unknown>;
            });
          })
          .catch(() => {
            // Best-effort; if this fails we keep the catalog (or 0) fallback.
          });
      }

      setEvalData(data);
      const uc = ucRaw;
      if (uc) {
        const catalogName = String(
          catalogRow?.name ?? peekCatalogBriefSnapshot(clientId, ucId)?.title ?? '',
        ).trim();
        const rawName = String(uc.name ?? uc.title ?? catalogName ?? '').trim();
        // Evaluation header often omits name; never accept "Use Case N" as real (DMD-1178/1180).
        const name =
          rawName && !isPlaceholderUseCaseTitle(rawName) ? rawName : '';
        if (name) {
          setResolvedUseCaseName((prev) =>
            !prev || isPlaceholderUseCaseTitle(prev) ? name : prev,
          );
        } else {
          void ensureCatalogBriefSnapshot(clientId, ucId).then((resolved) => {
            if (cancelled || !resolved?.title) return;
            setResolvedUseCaseName((prev) =>
              !prev || isPlaceholderUseCaseTitle(prev) ? resolved.title : prev,
            );
          });
        }
        const category = String(
          uc.category_name ?? uc.category ?? uc.categoryFit ?? catalogRow?.category_name ?? '',
        ).trim();
        if (category) setResolvedCategory((prev) => prev || category);
        if (typeof uc.total_score === 'number' && Number.isFinite(uc.total_score)) {
          setResolvedTotalScore((prev) => (prev == null ? (uc.total_score as number) : prev));
        }
        const band = String(uc.value_band ?? catalogRow?.value_band ?? '').trim();
        if (band) setResolvedValueBand((prev) => prev || band);
      }
      // Prefetch + align Financial Model NPV with ROI Model "Net 3-year value" (DMD-1093).
      void fetchRoiModel(clientId, ucId)
        .then((res) => {
          if (cancelled) return;
          const dollars = res?.data?.headerMetrics?.net3YearValueBase;
          if (typeof dollars === 'number' && Number.isFinite(dollars)) {
            setRoiModelNet3YearM(dollars / 1_000_000);
          }
        })
        .catch(() => {
          /* keep evaluation NPV if ROI model is unavailable */
        });
      void fetchScoringRationale(clientId, String(ucId));
    };

    const cached = peekEvaluation(clientId, ucId);
    if (cached?.data) {
      applyEval(cached.data as Record<string, unknown>);
      setEvalError(null);
      setEvalLoading(false);
      return;
    }

    setEvalLoading(true);
    setEvalError(null);
    fetchEvaluation(clientId, ucId)
      .then((res) => {
        if (cancelled) return;
        if (res?.data) {
          applyEval(res.data as Record<string, unknown>);
          return;
        }
        setEvalError('Evaluation API returned no data');
      })
      .catch((err) => {
        if (!cancelled) setEvalError(err.message || 'Evaluation API failed');
        console.warn('[UCE Evaluation] API unavailable:', err.message);
      })
      .finally(() => {
        if (!cancelled) setEvalLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, clientId, isNumericUseCaseId]);

  // Prev/next pager + score freshness metrics from catalog cache.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    const applyMetrics = (metrics?: { last_computed_at?: string | null; last_computed_source?: string | null }) => {
      if (!metrics) return;
      if (metrics.last_computed_at) setMetricsLastComputedAt(metrics.last_computed_at);
      if (metrics.last_computed_source != null) {
        setMetricsLastComputedSource(String(metrics.last_computed_source));
      }
    };

    const cached = peekOpportunities(String(clientId), UCE_CATALOG_FILTERS);
    if (cached) {
      applyMetrics(cached.metrics);
      if (rankedIds.length === 0) {
        setRankedIds((cached.data ?? []).map((o) => String(o.use_case_id)));
        setCatalogTotal(cached.pagination?.total || cached.data?.length || TOTAL_PORTFOLIO_SIZE);
      }
    }

    const needIds = rankedIds.length === 0;
    const needMetrics = !(cached?.metrics?.last_computed_at);
    if (!needIds && !needMetrics) return;

    fetchOpportunities(String(clientId), UCE_CATALOG_FILTERS)
      .then((res) => {
        if (cancelled) return;
        applyMetrics(res.metrics);
        if (needIds) {
          setRankedIds((res.data ?? []).map((o) => String(o.use_case_id)));
          setCatalogTotal(res.pagination?.total || res.data?.length || TOTAL_PORTFOLIO_SIZE);
        }
      })
      .catch(() => {
        if (!cancelled && needIds) setRankedIds([]);
      });
    return () => { cancelled = true; };
  }, [clientId, rankedIds.length]);

  // profile = API-first mapping. For API-backed ids, missing fields stay empty/"No value".
  const profile = useMemo((): ProfileData => {
    const noDataText = 'No value';

    const emptyProfile: ProfileData = {
      use_case_id: String(id || 'unknown'),
      title: (() => {
        const fromResolved =
          resolvedUseCaseName && !isPlaceholderUseCaseTitle(resolvedUseCaseName)
            ? resolvedUseCaseName
            : null;
        const fromCatalog = id ? peekCatalogBriefSnapshot(clientId, id)?.title : null;
        const catalogOk =
          fromCatalog && !isPlaceholderUseCaseTitle(fromCatalog) ? fromCatalog : null;
        return fromResolved || catalogOk || (id ? `Use Case ${id}` : 'Use Case');
      })(),
      category: resolvedCategory || '',
      priority_label: '',
      recommendation: 'EVALUATE',
      recommendation_from_api: false,
      executive_summary: noDataText,
      rationale: noDataText,
      use_case_description: noDataText,
      strategic_narrative: noDataText,
      strategic_context: noDataText,
      organizational_impact: noDataText,
      operational_impact: noDataText,
      clinical_impact: noDataText,
      stakeholder_alignment: noDataText,
      alignment_evidence: [],
      roi_range: noDataText,
      time_to_roi: noDataText,
      implementation_effort: 'Medium',
      value_score: 0,
      implementation_score: 0,
      total_score: 0,
      rank: 0,
      percentile: null,
      last_computed_at: null,
      last_computed_source: null,
      roi: {
        annual_benefit_m: null,
        implementation_cost_m: null,
        payback_months: null,
        npv_3yr_m: null,
        confidence: 0,
      },
      roi_narrative: noDataText,
      roi_assumptions: [],
      feasibility: {
        complexity_summary: noDataText,
        technology_readiness: noDataText,
        data_availability: noDataText,
        organizational_readiness: noDataText,
        integration_complexity: noDataText,
        operational_dependencies: noDataText,
        change_management: noDataText,
      },
      risks: [],
      milestones: [],
      requirements: [],
      next_steps: [],
      actions: { immediate: [], near_term: [], strategic: [] },
    };

    const generated = emptyProfile;
    if (!evalData) return generated;

    const uc = evalData.use_case || {};
    const tabs = evalData.tabs || {};
    const singleTabKey = evalData.tab as string | undefined;
    const singleTabContent = (evalData.content || {}) as any;
    const resolveTab = (key: string) => (tabs[key] || (singleTabKey === key ? singleTabContent : {})) as any;

    const overview = resolveTab('overview');
    const alignment = resolveTab('alignment');
    const roiSummary = resolveTab('roi_summary');
    const feasibility = resolveTab('feasibility');
    const risksTab = resolveTab('risks');
    const roadmapTab = resolveTab('roadmap');
    const actionsTab = resolveTab('actions');

    const keyMetrics = overview.key_metrics || {};

    const valueScore = keyMetrics.value_score ?? uc.value_score ?? generated.value_score;
    const implementationScore = keyMetrics.implementation_score ?? uc.implementation_score ?? generated.implementation_score;
    const totalScore = resolvedTotalScore ?? keyMetrics.total_score ?? uc.total_score ?? generated.total_score;
    const rank = keyMetrics.rank ?? uc.rank ?? generated.rank;

    // Explicit API recommendation wins; score thresholds are a flagged fallback (DMD-1160).
    const apiRec = parseRecommendation(uc.recommendation ?? evalData.recommendation);
    const rec: RecommendationKey =
      apiRec ?? recommendationFromVerdict(verdictFromScore(totalScore, resolvedValueBand ?? undefined));

    const rawLow = roiSummary.roi_low ?? roiSummary.annual_benefit_low ?? null;
    const rawHigh = roiSummary.roi_high ?? roiSummary.annual_benefit_high ?? null;
    const normM = (v: number) => v > 1_000 ? v / 1_000_000 : v;

    const mergeActions = (apiArr: any[], fallback: ProfileData['actions']['immediate']) => {
      const mapped = (apiArr || [])
        .map((a: any) => typeof a === 'string'
          ? { title: a, description: '', owner: 'Leadership Team' }
          : { title: a.title ?? '', description: a.description ?? '', owner: a.owner ?? 'Leadership Team' })
        .filter((a: any) => a.title);
      return mapped.length ? mapped : fallback;
    };

    const apiRisks = (risksTab.risks ?? risksTab.items ?? [])
      .map((r: any): RiskItem => {
        if (typeof r === 'string') {
          return {
            name: 'Risk',
            severity: 'Medium',
            likelihood: 'Medium',
            description: r,
            mitigation: '',
            owner: 'Leadership Team',
          };
        }
        const rawDesc = typeof r.description === 'string' ? r.description : '';
        const rawMit = typeof r.mitigation === 'string' ? r.mitigation : (typeof r.mitigation_strategy === 'string' ? r.mitigation_strategy : '');
        const same = rawDesc.trim() === rawMit.trim();
        return {
          risk_id: typeof r.risk_id === 'string' ? r.risk_id : (typeof r.riskId === 'string' ? r.riskId : undefined),
          name: r.name ?? 'Risk',
          severity: normalizeRiskSeverity(r.severity),
          likelihood: normalizeRiskLikelihood(r.likelihood),
          description: rawDesc || rawMit,
          mitigation: rawDesc && !same ? rawMit : '',
          owner: r.owner ?? 'Leadership Team',
        };
      });

    // DMD-1176: always sort by months_from_start — never trust API array order /
    // phase-name fallbacks that previously put Month 12 ahead of Month 1.
    const apiMilestones = sortMilestonesByTimeline(
      (roadmapTab.milestones ?? []).map((m: any): MilestoneItem => ({
        milestone_id: m.milestone_id ?? m.milestoneId ?? null,
        label: (m.label ?? m.name ?? `Phase ${m.phase ?? ''}`.trim()) || 'Milestone',
        months_from_start: parseMonthsFromStart(
          m && typeof m === 'object' ? (m as Record<string, unknown>) : {}
        ),
        value_note: m.value_note ?? m.description ?? m.relative_timing ?? m.relativeTiming ?? '',
        expected_outcome: m.expected_outcome ?? m.expectedOutcome ?? m.outcome ?? '',
        status: m.status ?? m.phase_status ?? m.tracking_status ?? null,
        percent_complete:
          typeof m.percent_complete === 'number'
            ? m.percent_complete
            : typeof m.percentComplete === 'number'
              ? m.percentComplete
              : typeof m.tracking_completion_pct === 'number'
                ? m.tracking_completion_pct
                : null,
        owner: m.owner ?? m.owner_role ?? 'Leadership Team',
      }))
    );

    // DMD-1171: preserve section → sub-step hierarchy from overview markdown / arrays.
    const stepCandidates: ImmediateNextStep[][] = [
      coerceImmediateNextSteps(overview.immediate_next_steps_markdown),
      coerceImmediateNextSteps(overview.next_steps),
      coerceImmediateNextSteps(overview.immediate_next_steps),
      coerceImmediateNextSteps(actionsTab.immediate),
      coerceImmediateNextSteps(actionsTab.items),
    ];

    const hierarchical = stepCandidates.find(
      (arr) => arr.length > 0 && arr.some((g) => g.items.length > 0)
    );
    const normalizedNextSteps: ImmediateNextStep[] =
      hierarchical ||
      stepCandidates.find((arr) => arr.length > 0) ||
      generated.next_steps;

    const fallbackRoiRange = rawLow != null && rawHigh != null
      ? `$${fmtM2(normM(rawLow))}M – $${fmtM2(normM(rawHigh))}M`
      : noDataText;

    const apiRequirements = mapRoadmapRequirements(roadmapTab, feasibility, evalData);

    // DMD-1173: keep stakeholder-relevant evidence only; drop phase/RACI/ops fragments.
    const alignmentEvidence = normalizeAlignmentEvidence(
      Array.isArray(alignment.evidence_points)
        ? alignment.evidence_points
        : Array.isArray(alignment.alignmentEvidence)
          ? alignment.alignmentEvidence
          : []
    );

    return {
      ...generated,
      use_case_id: String(uc.use_case_id ?? generated.use_case_id),
      title: (() => {
        const fromApi = String(uc.title ?? uc.name ?? '').trim();
        if (fromApi && !isPlaceholderUseCaseTitle(fromApi)) return fromApi;
        if (resolvedUseCaseName && !isPlaceholderUseCaseTitle(resolvedUseCaseName)) {
          return resolvedUseCaseName;
        }
        const fromCatalog = id ? peekCatalogBriefSnapshot(clientId, id)?.title : null;
        if (fromCatalog && !isPlaceholderUseCaseTitle(fromCatalog)) return fromCatalog;
        return fromApi || resolvedUseCaseName || generated.title;
      })(),
      category: pickProfileContent(
        uc.category,
        uc.category_name,
        resolvedCategory,
      ),
      priority_label: uc.priority_label ?? generated.priority_label,
      value_score: valueScore,
      implementation_score: implementationScore,
      total_score: totalScore,
      rank,
      percentile:
        typeof keyMetrics.percentile === 'number' && Number.isFinite(keyMetrics.percentile)
          ? keyMetrics.percentile
          : typeof uc.percentile === 'number' && Number.isFinite(uc.percentile)
            ? uc.percentile
            : null,
      last_computed_at: uc.last_computed_at ?? evalData.last_computed_at ?? null,
      last_computed_source: uc.last_computed_source ?? evalData.last_computed_source ?? null,
      implementation_effort: (uc.effort_tier as any) ?? generated.implementation_effort,
      recommendation: rec,
      recommendation_from_api: apiRec != null,
      // Overview tab
      executive_summary: overview.summary ?? overview.executive_summary ?? uc.description ?? noDataText,
      use_case_description: overview.use_case_description ?? overview.description ?? uc.description ?? noDataText,
      next_steps: normalizedNextSteps,
      strategic_context: pickProfileContent(overview.strategic_context, alignment.narrative) || noDataText,
      organizational_impact: pickProfileContent(
        alignment.organizational_impact,
        alignment.orgImpact,
        alignment.organizationalImpact,
        overview.organizational_impact,
      ),
      operational_impact: pickProfileContent(
        alignment.operational_impact,
        alignment.operationImpact,
        alignment.operationalImpact,
        overview.operational_impact,
      ),
      clinical_impact: pickProfileContent(
        alignment.clinical_impact,
        alignment.clinicalImpact,
        overview.clinical_impact,
        overview.clinicalImpact,
      ),
      rationale: overview.rationale ?? overview.executive_decision ?? noDataText,
      // Alignment tab
      strategic_narrative: alignment.narrative ?? alignment.strategic_narrative ?? noDataText,
      stakeholder_alignment: alignment.alignment_summary ?? alignment.stakeholder_alignment ?? noDataText,
      alignment_evidence: alignmentEvidence,
      // ROI tab
      roi_range: roiSummary.roi_range ?? fallbackRoiRange,
      time_to_roi: roiSummary.time_to_roi ?? uc.timeline ?? generated.time_to_roi,
      roi: {
        annual_benefit_m: roiSummary.annual_benefit_m ?? roiSummary.annual_benefit ?? generated.roi.annual_benefit_m,
        implementation_cost_m: roiSummary.implementation_cost_m ?? roiSummary.implementation_cost ?? generated.roi.implementation_cost_m,
        payback_months: roiSummary.payback_months ?? generated.roi.payback_months,
        // Prefer ROI Model computed net 3-year value over evaluation npv_base (DMD-1093).
        npv_3yr_m:
          roiModelNet3YearM ??
          roiSummary.npv_3yr_m ??
          roiSummary.npv_3yr ??
          generated.roi.npv_3yr_m,
        confidence: roiSummary.confidence ?? generated.roi.confidence,
      },
      roi_narrative: roiSummary.narrative ?? roiSummary.roi_narrative ?? noDataText,
      roi_assumptions: roiSummary.assumptions?.length ? roiSummary.assumptions : generated.roi_assumptions,
      // Feasibility tab
      feasibility: {
        complexity_summary: feasibility.complexity_summary ?? feasibility.implementation_timeline ?? noDataText,
        technology_readiness: feasibility.technology_readiness ?? noDataText,
        data_availability: feasibility.data_availability ?? noDataText,
        organizational_readiness: feasibility.organizational_readiness ?? noDataText,
        integration_complexity: feasibility.integration_complexity ?? noDataText,
        operational_dependencies: feasibility.operational_dependencies ?? noDataText,
        change_management: feasibility.change_management ?? noDataText,
      },
      // Risks tab
      risks: apiRisks.length ? apiRisks : generated.risks,
      // Roadmap tab
      milestones: apiMilestones.length
        ? apiMilestones
        : sortMilestonesByTimeline(generated.milestones),
      requirements: apiRequirements,
      // Actions tab
      actions: {
        immediate: mergeActions(actionsTab.immediate, generated.actions.immediate),
        near_term: mergeActions(actionsTab.near_term, generated.actions.near_term),
        strategic: mergeActions(actionsTab.strategic, generated.actions.strategic),
      },
    };
  }, [evalData, id, clientId, resolvedUseCaseName, resolvedCategory, resolvedTotalScore, resolvedValueBand, roiModelNet3YearM]);


  const restoreNav = navState.restore;
  // DMD-1186: ← back always exits to All Opportunities / Execution — not sibling tabs.
  const exitNav = resolveDrilldownExit(navState);
  const fromStep3 = exitNav.fromActivate;
  const backLabel = exitNav.label;
  const backPath = exitNav.path;

  const idx = rankedIds.indexOf(String(id || ''));
  const prevId = idx > 0 ? rankedIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < rankedIds.length - 1 ? rankedIds[idx + 1] : null;
  const pageTo = (targetId: string | null) => {
    if (!targetId) return;
    // DMD-1180: after ~60s the list TTL is cold — ensure a real title before navigate.
    void (async () => {
      const catalogSnap =
        peekCatalogBriefSnapshot(clientId, targetId) ||
        (await ensureCatalogBriefSnapshot(clientId, targetId));
      navigate(`/ea/use-case/${targetId}/brief`, {
        state: buildDrilldownNav(navState, {
          rankedIds,
          catalogTotal,
          briefSnapshot: catalogSnap,
        }),
      });
    })();
  };

  const drilldownState: EaDrilldownNavState = buildDrilldownNav(navState, {
    rankedIds,
    catalogTotal,
    // DMD-1186: do not overwrite entry `from` with this page path — keeps exit back stable.
    briefSnapshot: id
      ? {
          id: String(id),
          title: profile.title,
          category: profile.category,
          totalScore: profile.total_score,
          valueBand: resolvedValueBand ?? undefined,
        }
      : undefined,
  });

  const inPortfolio = Boolean(id) && finalistIds.has(String(id));
  const verdict = verdictFromRecommendation(profile.recommendation);
  const effortTag = EFFORT_TO_TAG[profile.implementation_effort] ?? 'Moderate';
  const portfolioSize = catalogTotal || TOTAL_PORTFOLIO_SIZE;
  // Percentile is shown only when the API reports it — never approximated from rank (DMD-1161).
  const percentile = profile.percentile;

  // Per-use-case freshness when the evaluation payload carries it, else portfolio catalog metrics.
  const scoreFreshness = resolveScoreFreshness(
    profile.last_computed_at ?? metricsLastComputedAt,
    profile.last_computed_source ?? metricsLastComputedSource,
  );
  const showFreshness = Boolean(scoreFreshness.label) || scoreFreshness.hasSource;

  const roadmap = profile.milestones.map((m) => ({
    milestoneId: m.milestone_id ?? null,
    phase: m.label,
    dur:
      m.months_from_start != null && Number.isFinite(m.months_from_start)
        ? `Month ${m.months_from_start}`
        : m.value_note || 'TBD',
    desc: m.expected_outcome || m.value_note || '',
    status:
      (m.milestone_id && milestoneStatusOverrides[m.milestone_id]) ||
      m.status,
    percentComplete: m.percent_complete,
  }));
  const roadmapProgress = roadmapProgressSummary(roadmap);
  const savingMilestoneIdSet = savingMilestoneIds;

  const handleMilestoneStatusChange = useCallback(
    async (milestoneId: string, status: RoadmapPhaseStatus) => {
      if (!clientId || !id) return;
      const useCaseId = parseInt(id, 10);
      if (Number.isNaN(useCaseId)) return;

      setMilestoneStatusError(null);
      setMilestoneStatusOverrides((prev) => ({ ...prev, [milestoneId]: status }));
      setSavingMilestoneIds((prev) => {
        const next = new Set(prev);
        next.add(milestoneId);
        return next;
      });

      try {
        await updateMilestone(String(clientId), useCaseId, milestoneId, {
          status: toApiMilestoneStatus(status),
          completion_pct: status === 'done' ? 100 : status === 'todo' ? 0 : undefined,
        });
        invalidateEvaluation(String(clientId), useCaseId);
      } catch (err) {
        setMilestoneStatusOverrides((prev) => {
          const next = { ...prev };
          delete next[milestoneId];
          return next;
        });
        const message =
          err instanceof CommandCenterError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Failed to update milestone status';
        setMilestoneStatusError(message);
        console.warn('[Path to Value] status update failed:', message);
      } finally {
        setSavingMilestoneIds((prev) => {
          const next = new Set(prev);
          next.delete(milestoneId);
          return next;
        });
      }
    },
    [clientId, id],
  );

  const alignmentCards = [
    { key: 'org', label: 'Organizational Impact', value: profile.organizational_impact },
    { key: 'clinical', label: 'Clinical Impact', value: profile.clinical_impact },
    { key: 'stakeholder', label: 'Stakeholder Alignment', value: profile.stakeholder_alignment },
  ].filter((card) => !isEmptyProfileContent(card.value));

  const handleBack = () => {
    navigate(backPath, {
      state: {
        restore: restoreNav,
        ...(fromStep3 ? {} : { returnToTab: 'explore' }),
      },
    });
  };

  const handleProceed = async () => {
    if (!id || !clientId) return;
    if (isFreemium) {
      navigate('/ea/upgrade');
      return;
    }
    if (inPortfolio) return;
    const ucId = parseInt(id, 10);
    if (Number.isNaN(ucId)) return;
    addFinalist(id);
    try {
      await selectUseCase(String(clientId), ucId);
      setSelectionSyncError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Selection update failed';
      console.warn('[UCE Selection] Backend update failed (local state kept):', message);
      setSelectionSyncError(message);
    }
  };

  const handleOpenCommandCenter = () => {
    if (!id) return;
    navigate(`/ea/execute/${id}`, {
      state: buildDrilldownNav(drilldownState, {
        restore: restoreNav ?? drilldownState?.restore,
      }),
    });
  };

  const handleHubTab = (view: HubView) => {
    const paths: Record<HubView, string> = {
      brief: `/ea/use-case/${String(id)}/brief`,
      rationale: `/ea/use-case/${String(id)}/rationale`,
      roi: `/ea/use-case/${String(id)}/roi`,
    };
    if (view === 'brief') return;
    navigate(paths[view], {
      state: buildDrilldownNav(drilldownState, {
        restore: restoreNav ?? drilldownState?.restore,
      }),
    });
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
      setShareLabel('Copied');
      window.setTimeout(() => setShareLabel('Share'), 1600);
    } catch {
      setShareLabel('Share');
    }
  };

  const handleExport = () => {
    const section = (title: string, body: string): { title: string; body: string } | null => {
      const text = String(body ?? '').trim();
      if (!text || isEmptyProfileContent(text)) return null;
      return { title, body: text };
    };

    const alignmentBodies = [
      ['Organizational Impact', profile.organizational_impact],
      ['Clinical Impact', profile.clinical_impact],
      ['Stakeholder Alignment', profile.stakeholder_alignment],
    ]
      .filter(([, v]) => !isEmptyProfileContent(v))
      .map(([label, v]) => `### ${label}\n\n${v}`)
      .join('\n\n');

    const evidenceBodies = profile.alignment_evidence
      .map((ev) => {
        const label = ev.label ? `**${ev.label}** — ` : '';
        return `- ${label}${ev.markdown}`;
      })
      .join('\n');

    const nextStepsBody = profile.next_steps
      .map((step, i) => {
        const items = (step.items ?? []).map((it) => `  - ${it}`).join('\n');
        return `${i + 1}. **${step.title}**${items ? `\n${items}` : ''}`;
      })
      .join('\n');

    const sections = [
      section('Executive Summary', profile.executive_summary),
      section('Recommendation rationale', profile.rationale),
      section('Strategic Context', profile.strategic_context),
      section('Strategic Narrative', profile.strategic_narrative),
      section('Strategic Alignment', alignmentBodies),
      section('Alignment Evidence', evidenceBodies),
      section('Initiative Description', profile.use_case_description),
      section('Immediate Next Steps', nextStepsBody),
      section(
        'Financial Model',
        [
          `- Annual Benefit: ${fmtKpiAnnualBenefit(profile.roi_range, profile.roi.annual_benefit_m)}`,
          `- 3-Year NPV: ${fmtKpiMoneyM(profile.roi.npv_3yr_m)}`,
          `- Implementation Cost: ${fmtKpiMoneyM(profile.roi.implementation_cost_m)}`,
          `- Payback: ${
            profile.roi.payback_months != null ? `${profile.roi.payback_months} mo` : KPI_UNAVAILABLE
          }`,
        ].join('\n'),
      ),
      section('Scoring Profile', [
        `- Business Value: ${fmtKpiScore(profile.value_score)}`,
        `- Implementation Feasibility: ${fmtKpiScore(profile.implementation_score)}`,
        `- Composite: ${fmtKpiScore(profile.total_score)}`,
      ].join('\n')),
      section('ROI Narrative', profile.roi_narrative),
    ].filter((s): s is { title: string; body: string } => s != null);

    setPrintSnapshot({
      title: profile.title,
      category: profile.category,
      recommendation: profile.recommendation,
      recommendationIsDerived: !profile.recommendation_from_api,
      metrics: [
        { label: 'Annual Benefit', value: fmtKpiAnnualBenefit(profile.roi_range, profile.roi.annual_benefit_m) },
        { label: '3-Year NPV', value: fmtKpiMoneyM(profile.roi.npv_3yr_m) },
        { label: 'Time to Value', value: fmtKpiTimeToValue(profile.time_to_roi) },
        { label: 'Composite Score', value: fmtKpiScore(profile.total_score) },
        { label: 'Portfolio Rank', value: fmtKpiRank(profile.rank, portfolioSize) },
      ],
      scoreFreshnessLabel: scoreFreshness.label,
      scoreIsLive: scoreFreshness.isLive,
      sections,
      risks: profile.risks.map((r) => ({
        title: r.name,
        meta: `${r.severity} · ${r.likelihood}`,
        detail: [r.description, r.mitigation ? `Mitigation: ${r.mitigation}` : '', r.owner ? `Owner: ${r.owner}` : '']
          .filter(Boolean)
          .join('\n'),
      })),
      milestones: profile.milestones.map((m) => ({
        title: m.label,
        meta: m.months_from_start != null ? `Month ${m.months_from_start}` : undefined,
        detail: [m.expected_outcome || m.value_note, m.status ? `Status: ${m.status}` : '']
          .filter(Boolean)
          .join('\n'),
      })),
      requirements: profile.requirements.map((r) => ({
        title: r.name,
        meta: r.critical ? 'Critical' : r.type,
        detail: r.details,
      })),
    });
    setPrintGeneratedAt(new Date());
    setPrintPreviewOpen(true);
  };

  const showProfileLoading = isNumericUseCaseId && evalLoading && !evalData;

  // Publish frozen hub chrome when embedded (Brief is canonical — force overwrite bootstrap).
  useEffect(() => {
    if (!embedded || !id || showProfileLoading) return;
    if (!evalData) return;

    const freshnessLabel = showFreshness
      ? [
          scoreFreshness.hasSource
            ? scoreFreshness.isLive
              ? 'Live-computed'
              : 'Prior computation'
            : null,
          scoreFreshness.label,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

    setChrome(
      {
        useCaseId: String(id),
        title: profile.title,
        category: normalizePriorityLabel(profile.category),
        rank: profile.rank,
        portfolioSize,
        effortTag,
        verdict,
        decisionContent: isEmptyProfileContent(profile.rationale) ? (
          <span>
            {verdict === 'green'
              ? 'Proceed: scores and strategic fit support carrying this initiative forward.'
              : verdict === 'cond'
                ? 'Validate first: confirm key assumptions before committing implementation capacity.'
                : 'Hold: revisit later unless constraints or scores improve.'}
          </span>
        ) : (
          <ProfileMarkdown source={profile.rationale} />
        ),
        bannerFooter: !profile.recommendation_from_api ? (
          <p className="mt-1.5 text-[10px] italic text-[#717182]">
            Verdict derived from the composite score — the evaluation payload carries no explicit
            recommendation.
          </p>
        ) : undefined,
        kpis: {
          annualBenefit: fmtKpiAnnualBenefit(profile.roi_range, profile.roi.annual_benefit_m),
          npv: fmtKpiMoneyM(profile.roi.npv_3yr_m),
          timeToValue: fmtKpiTimeToValue(profile.time_to_roi),
          compositeScore: fmtKpiScore(profile.total_score),
          portfolioRank: fmtKpiRank(profile.rank, portfolioSize),
        },
        freshnessLabel,
        freshnessIsLive: scoreFreshness.isLive,
        freshnessTitle: scoreFreshness.absoluteTitle,
        recommendationDerived: !profile.recommendation_from_api,
      },
      { force: true },
    );
    // Primitive deps only — avoid `profile` / `scoreFreshness` object identity loops.
  }, [
    embedded,
    id,
    showProfileLoading,
    evalData,
    profile.title,
    profile.category,
    profile.rank,
    profile.rationale,
    profile.roi_range,
    profile.roi.annual_benefit_m,
    profile.roi.npv_3yr_m,
    profile.time_to_roi,
    profile.total_score,
    profile.recommendation_from_api,
    portfolioSize,
    effortTag,
    verdict,
    showFreshness,
    scoreFreshness.hasSource,
    scoreFreshness.isLive,
    scoreFreshness.label,
    scoreFreshness.absoluteTitle,
    setChrome,
  ]);

  // Register export via ref — no context state updates, no render loops.
  useEffect(() => {
    if (!embedded) return;
    setExportHandler(() => handleExport);
    return () => setExportHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleExport closes over latest profile
  }, [embedded, setExportHandler, profile.title]);

  // Hub asked us to open export (Export PDF from another tab).
  useEffect(() => {
    if (!embedded || !navState.openExport) return;
    if (showProfileLoading || !evalData) return;
    handleExport();
    navigate(location.pathname, {
      replace: true,
      state: buildDrilldownNav(navState, { openExport: false }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once when flag is set
  }, [embedded, navState.openExport, showProfileLoading, evalData]);

  if (!id || !isNumericUseCaseId) {
    if (embedded) {
      return (
        <EAApiErrorPanel
          title="Use case not found"
          message="This profile requires a numeric use case ID from the Use Case Explorer."
        />
      );
    }
    return (
      <div className="min-h-screen bg-[#F5F7FA]">
        <div className="px-6 pt-3 pb-1"> {/* DMD-1168: tighter wrap */}
          <EAJourneyBar />
        </div>
        <main className="px-6 py-5">
          <EAApiErrorPanel
            title="Use case not found"
            message="This profile requires a numeric use case ID from the Use Case Explorer."
          />
        </main>
      </div>
    );
  }

  if (!evalLoading && evalError && !evalData) {
    if (embedded) {
      return (
        <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-10 text-center">
          <p className="text-sm font-semibold text-[#18365E] mb-1">Use case profile not available</p>
          <p className="text-xs text-[#717182] max-w-sm mx-auto leading-relaxed">
            This profile is not available for this use case yet. Return to the opportunities list to
            explore other use cases.
          </p>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#F5F7FA]">
        <div className="px-6 pt-3 pb-1"> {/* DMD-1168: tighter wrap */}
          <EAJourneyBar />
        </div>
        <div className="sticky top-[72px] z-10 bg-white/95 backdrop-blur border-b border-[#e5e7eb] px-6 py-2.5">
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors shrink-0"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {backLabel}
            </button>
          </div>
        </div>
        <main className="px-6 py-4">
          <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-10 text-center">
            <p className="text-sm font-semibold text-[#18365E] mb-1">Use case profile not available</p>
            <p className="text-xs text-[#717182] max-w-sm mx-auto leading-relaxed">
              This profile is not available for this use case yet. Return to the opportunities list to explore other use cases.
            </p>
          </div>
        </main>
      </div>
    );
  }

  const briefBody = showProfileLoading ? (
    <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm p-10 text-center">
      <div className="w-8 h-8 border-2 border-[#18365E]/20 border-t-[#18365E] rounded-full animate-spin mx-auto mb-3" />
      <p className="text-sm font-medium text-[#18365E]">Loading use case profile...</p>
      <p className="text-xs text-[#717182] mt-1">Fetching evaluation tabs and scores</p>
    </div>
  ) : (
    <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
              <div className="space-y-4">
                <SectionCard icon={<FileText className="w-3.5 h-3.5" />} title="Executive Summary">
                  <ProfileMarkdown source={profile.executive_summary} />
                </SectionCard>

                <SectionCard icon={<Zap className="w-3.5 h-3.5" />} title="Immediate Next Steps" accent>
                  <StepList steps={profile.next_steps} />
                </SectionCard>

                <Collapse icon={<BookOpen className="w-3.5 h-3.5" />} title="Strategic Context" defaultOpen>
                  <ProfileMarkdown source={profile.strategic_context} />
                </Collapse>

                <Collapse
                  icon={<Target className="w-3.5 h-3.5" />}
                  title="Strategic Alignment"
                  meta={
                    alignmentCards.length
                      ? `${alignmentCards.length} dimension${alignmentCards.length === 1 ? '' : 's'}`
                      : undefined
                  }
                  defaultOpen
                >
                  {!isEmptyProfileContent(profile.strategic_narrative) && (
                    <div className="mb-3">
                      <ProfileMarkdown source={profile.strategic_narrative} />
                    </div>
                  )}
                  {alignmentCards.length > 0 ? (
                    <div
                      className={`grid gap-3 ${
                        alignmentCards.length === 1
                          ? 'grid-cols-1'
                          : 'sm:grid-cols-2'
                      }`}
                    >
                      {alignmentCards.map((card) => (
                        <div
                          key={card.key}
                          className="rounded-lg border border-[#e5e7eb] bg-[#F5F7FA] p-3"
                        >
                          <p className="text-[10px] font-bold uppercase tracking-wide text-[#18365E] mb-1.5">
                            {card.label}
                          </p>
                          <ProfileMarkdown source={card.value} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#717182] italic">
                      Strategic alignment details are not yet available for this use case.
                    </p>
                  )}
                  {profile.alignment_evidence.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-semibold text-[#18365E]">Alignment evidence</p>
                      <p className="text-[11px] text-[#717182]">
                        Evidence that supports stakeholder alignment for this initiative.
                      </p>
                      {profile.alignment_evidence.map((ev, i) => (
                        <div key={`ev-${i}`} className="rounded-lg border border-[#e5e7eb] p-3">
                          <div className="mb-1.5 flex flex-wrap items-center gap-2">
                            {ev.category && (
                              <span className="inline-flex rounded bg-[#F5F7FA] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#18365E]">
                                {ev.category}
                              </span>
                            )}
                            {ev.priority && (
                              <span className="inline-flex text-[10px] font-semibold uppercase tracking-wide text-[#FF6E4C]">
                                {ev.priority}
                              </span>
                            )}
                          </div>
                          {ev.label && (
                            <p className="text-sm font-semibold text-[#18365E] mb-1">{ev.label}</p>
                          )}
                          <ProfileMarkdown source={ev.markdown} />
                        </div>
                      ))}
                    </div>
                  )}
                </Collapse>

                <Collapse icon={<FileText className="w-3.5 h-3.5" />} title="Initiative Description & Operational Impact">
                  <ProfileMarkdown source={profile.use_case_description} />
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-[#18365E] mb-1">Operational impact</p>
                    <ProfileMarkdown
                      source={profile.operational_impact}
                      emptyMessage="Operational impact has not been documented for this use case yet."
                    />
                  </div>
                </Collapse>

                <Collapse
                  icon={<Shield className="w-3.5 h-3.5" />}
                  title="Risk Register"
                  meta={
                    profile.risks.length
                      ? `${profile.risks.length} identified · top severity ${topRiskSeverity(profile.risks) ?? '—'}`
                      : 'None documented'
                  }
                >
                  {profile.risks.length === 0 ? (
                    <p className="text-sm text-[#717182] italic">No risks are documented for this use case yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {profile.risks.map((risk, i) => (
                        <RiskCard key={risk.risk_id || `${risk.name}-${i}`} risk={risk} index={i} />
                      ))}
                    </div>
                  )}
                </Collapse>

                <Collapse
                  icon={<Calendar className="w-3.5 h-3.5" />}
                  title="Implementation Roadmap"
                  meta={roadmapProgress.meta}
                >
                  {roadmap.length > 0 && roadmapProgress.pct != null && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-[10px] text-[#717182] mb-1">
                        <span>Timeline progress</span>
                        <span className="font-semibold text-[#18365E]">{roadmapProgress.pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[#F5F7FA] border border-[#e5e7eb] overflow-hidden">
                        <div
                          className="h-full bg-[#18365E]"
                          style={{ width: `${roadmapProgress.pct}%` }}
                        />
                      </div>
                    </div>
                  )}
                  <RoadmapRow
                    roadmap={roadmap}
                    onStatusChange={handleMilestoneStatusChange}
                    savingIds={savingMilestoneIdSet}
                  />
                  {milestoneStatusError && (
                    <p className="text-[11px] text-red-600 mt-2">{milestoneStatusError}</p>
                  )}
                </Collapse>

                <Collapse
                  icon={<AlertTriangle className="w-3.5 h-3.5" />}
                  title="Implementation Requirements"
                  meta={
                    profile.requirements.length
                      ? `${profile.requirements.length} item${profile.requirements.length === 1 ? '' : 's'}`
                      : 'None defined'
                  }
                >
                  {profile.requirements.length === 0 ? (
                    <p className="text-sm text-[#717182] italic">
                      Implementation requirements have not been defined for this use case yet.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {profile.requirements.map((req, idx) => (
                        <div
                          key={`${req.name}-${idx}`}
                          className={`flex items-start gap-3 p-3 rounded-lg border ${
                            req.critical ? 'border-red-200 bg-red-50' : 'border-[#e5e7eb] bg-[#F5F7FA]'
                          }`}
                        >
                          <div
                            className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                              req.critical ? 'bg-red-500' : 'bg-slate-400'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-[#18365E]">{req.name}</p>
                              {req.critical && (
                                <span className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-semibold">
                                  Critical
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[#717182] mt-0.5 leading-relaxed">{req.details}</p>
                            <p className="text-[10px] text-[#717182] capitalize mt-1">{req.type} requirement</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Collapse>
              </div>

              <div className="space-y-4">
                <SectionCard icon={<BarChart3 className="w-3.5 h-3.5" />} title="Scoring Profile">
                  <div className="space-y-3">
                    <ScoreBar label="Business Value" value={profile.value_score} />
                    <ScoreBar label="Implementation Feasibility" value={profile.implementation_score} color="#FF6E4C" />
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {scoreFreshness.hasSource && (
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${
                          scoreFreshness.isLive
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        {scoreFreshness.isLive ? 'Live' : 'Prior'}
                      </span>
                    )}
                    {scoreFreshness.label && (
                      <span className="text-[10px] text-[#717182]" title={scoreFreshness.absoluteTitle}>
                        {scoreFreshness.label}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#717182] mt-3">
                    Weighted blend calibrated to your stated priorities. Rank #{profile.rank} of {portfolioSize}
                    {percentile != null ? ` — ${Math.round(percentile)}th percentile.` : '.'}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/ea/use-case/${id}/rationale`, { state: drilldownState })}
                    className="mt-3 text-xs font-semibold text-[#18365E] hover:text-[#FF6E4C] flex items-center gap-1 transition-colors"
                  >
                    Scoring rationale
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </SectionCard>

                <SectionCard icon={<TrendingUp className="w-3.5 h-3.5" />} title="Financial Model" accent>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-[#e5e7eb] bg-white p-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">Annual Benefit</p>
                      <p className="text-sm font-bold text-[#18365E]">
                        {fmtKpiAnnualBenefit(profile.roi_range, profile.roi.annual_benefit_m)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-[#e5e7eb] bg-white p-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">3-Year NPV</p>
                      <p className="text-sm font-bold text-[#18365E]">{fmtKpiMoneyM(profile.roi.npv_3yr_m)}</p>
                    </div>
                    <div className="rounded-lg border border-[#e5e7eb] bg-white p-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">Impl. Cost</p>
                      <p className="text-sm font-bold text-[#18365E]">{fmtKpiMoneyM(profile.roi.implementation_cost_m)}</p>
                    </div>
                    <div className="rounded-lg border border-[#e5e7eb] bg-white p-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#717182]">Payback</p>
                      <p className="text-sm font-bold text-[#18365E]">
                        {profile.roi.payback_months != null
                          ? `${profile.roi.payback_months} mo`
                          : KPI_UNAVAILABLE}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/ea/use-case/${id}/roi`, { state: drilldownState })}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-[#18365E] rounded-lg px-3 py-2 hover:bg-[#122a4a] transition-colors"
                  >
                    Open ROI Model
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </SectionCard>

                <SectionCard icon={<Shield className="w-3.5 h-3.5" />} title="Risk Digest" accent>
                  {profile.risks.length === 0 ? (
                    <p className="text-xs text-[#717182] italic">No risks documented.</p>
                  ) : (
                    <ul className="space-y-2">
                      {profile.risks.map((risk, i) => (
                        <li key={risk.risk_id || `${risk.name}-${i}`} className="flex items-center gap-2 text-xs text-[#4b5563]">
                          <span className={`w-2 h-2 rounded-full shrink-0 ${SEVERITY_DOT[risk.severity] ?? 'bg-slate-400'}`} />
                          <span className="flex-1">{risk.name}</span>
                          <span className="text-[10px] font-semibold text-[#717182]">{risk.severity}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[10px] text-[#717182] mt-3">Full register with mitigations in the Risk section.</p>
                </SectionCard>

                <SectionCard icon={<Calendar className="w-3.5 h-3.5" />} title="Path to Value">
                  <PathToValueList
                    roadmap={roadmap}
                    onStatusChange={handleMilestoneStatusChange}
                    savingIds={savingMilestoneIdSet}
                  />
                  {milestoneStatusError && (
                    <p className="text-[11px] text-red-600 mt-2">{milestoneStatusError}</p>
                  )}
                </SectionCard>
              </div>
            </div>
  );

  const printPreview = (
      <BriefPrintPreview
        open={printPreviewOpen && Boolean(printSnapshot)}
        onClose={() => {
          setPrintPreviewOpen(false);
          setPrintSnapshot(null);
          setPrintGeneratedAt(null);
        }}
        snapshot={
          printSnapshot ?? {
            title: profile.title,
            category: profile.category,
            recommendation: profile.recommendation,
            metrics: [],
            sections: [],
          }
        }
        generatedAt={printGeneratedAt ?? undefined}
      />
  );

  if (embedded) {
    return (
      <>
        <div className={printPreviewOpen ? 'print:hidden' : undefined}>{briefBody}</div>
        {printPreview}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className={printPreviewOpen ? 'print:hidden' : undefined}>
        <div className="px-6 pt-3 pb-1 print:hidden"> {/* DMD-1168: tighter wrap */}
          <EAJourneyBar />
        </div>

        {showProfileLoading ? (
          <div className="max-w-6xl mx-auto px-6 py-6">{briefBody}</div>
        ) : (
          <EAUseCaseHubShell
            activeView="brief"
            title={profile.title}
            category={normalizePriorityLabel(profile.category)}
            rank={profile.rank}
            portfolioSize={portfolioSize}
            effortTag={effortTag}
            inPortfolio={inPortfolio}
            verdict={verdict}
            decisionContent={
              isEmptyProfileContent(profile.rationale) ? (
                <span>
                  {verdict === 'green'
                    ? 'Proceed: scores and strategic fit support carrying this initiative forward.'
                    : verdict === 'cond'
                      ? 'Validate first: confirm key assumptions before committing implementation capacity.'
                      : 'Hold: revisit later unless constraints or scores improve.'}
                </span>
              ) : (
                <ProfileMarkdown source={profile.rationale} />
              )
            }
            bannerFooter={
              <>
                {selectionSyncError && (
                  <p className="mt-2 text-[10px] text-amber-700">
                    Portfolio saved locally. Sync issue: {selectionSyncError}
                  </p>
                )}
                {!profile.recommendation_from_api && (
                  <p className="mt-1.5 text-[10px] italic text-[#717182]">
                    Verdict derived from the composite score — the evaluation payload carries no
                    explicit recommendation.
                  </p>
                )}
              </>
            }
            kpis={{
              annualBenefit: fmtKpiAnnualBenefit(profile.roi_range, profile.roi.annual_benefit_m),
              npv: fmtKpiMoneyM(profile.roi.npv_3yr_m),
              timeToValue: fmtKpiTimeToValue(profile.time_to_roi),
              compositeScore: fmtKpiScore(profile.total_score),
              portfolioRank: fmtKpiRank(profile.rank, portfolioSize),
            }}
            freshnessLabel={
              showFreshness
                ? [
                    scoreFreshness.hasSource
                      ? scoreFreshness.isLive
                        ? 'Live-computed'
                        : 'Prior computation'
                      : null,
                    scoreFreshness.label,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                : null
            }
            freshnessIsLive={scoreFreshness.isLive}
            freshnessTitle={scoreFreshness.absoluteTitle}
            backLabel={backLabel}
            onBack={handleBack}
            prevId={prevId}
            nextId={nextId}
            onPage={pageTo}
            onTab={handleHubTab}
            onShare={() => {
              void handleShare();
            }}
            onExport={handleExport}
            exportLabel="Export PDF"
            onProceed={() => {
              void handleProceed();
            }}
            onOpenCommandCenter={handleOpenCommandCenter}
          >
            {briefBody}
          </EAUseCaseHubShell>
        )}
      </div>
      {printPreview}
    </div>
  );
}
